import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { WORLDS, type WorldSlug } from '@/lib/worlds';

/**
 * Card for the Story-Ideen section on the Inspiration-Tab.
 *
 * SI-Inspiration redesign 2026-05-11 (Sebastian Gate-1, Decision Q3
 * Option D): replaces the prior 140×140 horizontal-carousel tile
 * with a vertical-list larger-card pattern, visually symmetric with
 * FeatureCard (white card, dream-gold border, p-4, rounded). Adds a
 * subtitle body slot for the locked content's 1-2 sentence
 * descriptions, plus a world-chip below the body.
 *
 * Tap navigates to `/builder/${world}` (NO ?theme= query param —
 * SI-Inspiration Decision 3B: tap is plain world navigation, not a
 * theme prefill). The original prefill consumer in the SB builder
 * is removed by the same SI-Inspiration commit.
 *
 * Component name + filename intentionally kept as IdeaTile (rather
 * than renamed to IdeaCard) to minimize unrelated file churn — the
 * underlying primitive is what changed, not the slot it fills in
 * InspirationRoute.
 */
export interface IdeaTileProps {
  emoji: string;
  /** Card title — short noun-phrase or question. */
  name: string;
  /** Body subtitle — 1-2 sentences setting the scene. */
  subtitle: string;
  world: WorldSlug;
  className?: string;
  testId?: string;
}

export function IdeaTile({ emoji, name, subtitle, world, className, testId }: IdeaTileProps) {
  const navigate = useNavigate();
  const worldName = WORLDS[world.toUpperCase() as Uppercase<WorldSlug>].name;

  const handleClick = () => {
    navigate(`/builder/${world}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full flex-col items-start gap-3 rounded-lg border border-dream-gold bg-dream-white p-4 text-left transition-colors hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50',
        className,
      )}
      data-testid={testId}
      aria-label={`${name} — ${worldName}`}
    >
      <span aria-hidden className="text-2xl leading-none">
        {emoji}
      </span>
      <div className="flex w-full flex-col items-start gap-2">
        <p className="text-sm font-semibold leading-tight text-dream-navy">{name}</p>
        <p className="w-full text-xs leading-5 text-dream-navy opacity-50">{subtitle}</p>
      </div>
      <span className="rounded-full bg-blue-bg px-2 py-0.5 text-xxs font-medium text-dream-navy">
        {worldName}
      </span>
    </button>
  );
}
